<template>
    <div class="min-h-screen w-full flex flex-col bg-gradient-to-br from-slate-100 to-emerald-50">
        <div class="container mx-auto">
            <div class="px-4 sm:px-8 flex flex-col w-full pb-4 sm:pb-8 mt-4 sm:mt-8">
                <div class="mb-4">
                    <bread-crumb :items="breadCrumbItems"></bread-crumb>
                </div>

                <section class="proc-hero w-full rounded-2xl border shadow-lg p-6 md:p-8 mb-6">
                    <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                        <div class="min-w-0 flex-1">
                            <p class="proc-hero__eyebrow">ADPOLY · {{ campusTitle }}</p>
                            <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mt-1">Process</h1>
                            <p class="mt-2 text-gray-600 max-w-2xl leading-relaxed">
                                Campus process collections with labeled PDF and image attachments — browse by item, preview instantly.
                            </p>
                        </div>
                        <div class="flex flex-wrap items-center gap-2">
                            <div class="proc-stat">
                                <span class="proc-stat__value">{{ formatNumber(overview.items_count) }}</span>
                                <span class="proc-stat__label">Items</span>
                            </div>
                            <div class="proc-stat">
                                <span class="proc-stat__value">{{ formatNumber(overview.attachments_count) }}</span>
                                <span class="proc-stat__label">Files</span>
                            </div>
                            <button
                                v-if="canCreate"
                                type="button"
                                class="proc-btn proc-btn--primary"
                                @click="openCreate"
                            >
                                <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                                Add
                            </button>
                        </div>
                    </div>
                </section>

                <div v-if="pageLoading" class="flex justify-center py-20">
                    <base-page-spinner type="spin" class-list="h-16"></base-page-spinner>
                </div>

                <div v-else-if="showEmptyState" class="proc-empty">
                    <div class="proc-empty__icon">
                        <font-awesome-icon :icon="['fas', 'project-diagram']" />
                    </div>
                    <h2 class="proc-empty__title">No process items yet</h2>
                    <p class="proc-empty__text">
                        Create collections such as “Workflow Library”, “Student Service Forms”, or any custom name.
                        You can add labeled PDF/image attachments now or later.
                    </p>
                    <button
                        v-if="canCreate"
                        type="button"
                        class="proc-btn proc-btn--primary mt-4"
                        @click="openCreate"
                    >
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add
                    </button>
                </div>

                <div v-else class="proc-shell">
                    <aside class="proc-rail">
                        <div class="proc-rail__head">
                            <div class="proc-search">
                                <font-awesome-icon :icon="['fas', 'search']" class="proc-search__icon" />
                                <input
                                    v-model="searchText"
                                    type="search"
                                    class="proc-search__input"
                                    placeholder="Search items..."
                                    @input="onSearchInput"
                                />
                            </div>
                        </div>
                        <div class="proc-rail__list">
                            <button
                                v-for="item in items"
                                :key="item.id"
                                type="button"
                                class="proc-rail__item"
                                :class="{ 'proc-rail__item--active': selectedId === item.id }"
                                @click="selectItem(item.id)"
                            >
                                <div class="proc-rail__item-top">
                                    <span class="proc-rail__title">{{ item.title }}</span>
                                    <span class="proc-rail__count">{{ attachmentCount(item) }}</span>
                                </div>
                                <p v-if="item.description" class="proc-rail__desc">{{ item.description }}</p>
                                <div v-if="item.created_at || creatorName(item)" class="proc-rail__meta">
                                    <font-awesome-icon :icon="['fas', 'user-circle']" class="proc-rail__meta-icon" />
                                    <span>{{ creatorName(item) }}</span>
                                    <span v-if="item.created_at && creatorName(item)" class="proc-rail__meta-dot">·</span>
                                    <span v-if="item.created_at">{{ formatMetaDate(item.created_at) }}</span>
                                </div>
                            </button>
                            <div v-if="!items.length" class="proc-rail__empty">
                                No items match your search.
                            </div>
                        </div>
                        <div
                            v-if="paginatorInfo && paginatorInfo.lastPage > 1"
                            class="proc-rail__pager"
                        >
                            <pagination
                                :paginatorInfo="paginatorInfoWithItems"
                                :setPage="setPage"
                            ></pagination>
                        </div>
                    </aside>

                    <section class="proc-stage">
                        <template v-if="selectedItem">
                            <div class="proc-stage__header">
                                <div class="min-w-0 flex-1">
                                    <h2 class="proc-stage__title">{{ selectedItem.title }}</h2>
                                    <p v-if="selectedItem.description" class="proc-stage__desc">
                                        {{ selectedItem.description }}
                                    </p>
                                    <div
                                        v-if="selectedItem.created_at || creatorName(selectedItem)"
                                        class="proc-stage__meta"
                                    >
                                        <div class="proc-meta-chip">
                                            <span class="proc-meta-chip__icon-wrap">
                                                <font-awesome-icon :icon="['fas', 'user']" />
                                            </span>
                                            <span class="proc-meta-chip__text">
                                                Created by
                                                <strong>{{ creatorName(selectedItem) }}</strong>
                                            </span>
                                        </div>
                                        <div v-if="selectedItem.created_at" class="proc-meta-chip">
                                            <span class="proc-meta-chip__icon-wrap proc-meta-chip__icon-wrap--time">
                                                <font-awesome-icon :icon="['fas', 'clock']" />
                                            </span>
                                            <span class="proc-meta-chip__text">{{ formatMetaDateTime(selectedItem.created_at) }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="canDownloadProcessItem(selectedItem) || canUpdateItem(selectedItem) || canDeleteItem(selectedItem)"
                                    class="proc-menu"
                                    ref="actionsMenu"
                                >
                                    <button
                                        type="button"
                                        class="proc-menu__trigger"
                                        :class="{ 'proc-menu__trigger--open': actionsMenuOpen }"
                                        title="More actions"
                                        @click.stop="toggleActionsMenu"
                                    >
                                        <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
                                    </button>
                                    <div v-if="actionsMenuOpen" class="proc-menu__dropdown">
                                        <button
                                            v-if="canDownloadProcessItem(selectedItem)"
                                            type="button"
                                            class="proc-menu__item"
                                            :disabled="downloadingAll"
                                            @click.stop="onMenuDownloadAll"
                                        >
                                            <font-awesome-icon
                                                :icon="['fas', downloadingAll ? 'spinner' : 'download']"
                                                :spin="downloadingAll"
                                                class="proc-menu__item-icon"
                                            />
                                            {{ downloadingAll ? 'Downloading…' : 'Download' }}
                                        </button>
                                        <button
                                            v-if="canUpdateItem(selectedItem)"
                                            type="button"
                                            class="proc-menu__item"
                                            @click.stop="onMenuEdit"
                                        >
                                            <font-awesome-icon :icon="['fas', 'pen']" class="proc-menu__item-icon" />
                                            Edit
                                        </button>
                                        <button
                                            v-if="canDeleteItem(selectedItem)"
                                            type="button"
                                            class="proc-menu__item proc-menu__item--danger"
                                            @click.stop="onMenuDelete"
                                        >
                                            <font-awesome-icon :icon="['fas', 'trash']" class="proc-menu__item-icon" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="proc-gallery">
                                <div
                                    v-for="att in selectedItem.attachments || []"
                                    :key="att.id"
                                    class="proc-tile"
                                >
                                    <button
                                        type="button"
                                        class="proc-tile__hit"
                                        @click="openPreview(att)"
                                    >
                                        <div class="proc-tile__media">
                                            <img
                                                v-if="att.is_image && attachmentDisplayUrl(att)"
                                                :src="attachmentDisplayUrl(att)"
                                                :alt="att.label"
                                                class="proc-tile__img"
                                            />
                                            <div v-else-if="att.is_image && attachmentThumbLoading(att.id)" class="proc-tile__loading">
                                                <font-awesome-icon :icon="['fas', 'spinner']" spin class="proc-tile__loading-icon" />
                                            </div>
                                            <div v-else class="proc-tile__pdf">
                                                <font-awesome-icon :icon="['fas', 'file-pdf']" class="proc-tile__pdf-icon" />
                                                <span>PDF</span>
                                            </div>
                                            <div class="proc-tile__overlay">
                                                <font-awesome-icon :icon="['fas', 'search-plus']" />
                                                <span>Preview</span>
                                            </div>
                                        </div>
                                        <div class="proc-tile__caption">
                                            <div class="proc-tile__label">{{ att.label }}</div>
                                            <div class="proc-tile__meta">
                                                {{ att.is_pdf ? 'PDF' : 'Image' }}
                                                <span v-if="att.formatted_file_size"> · {{ att.formatted_file_size }}</span>
                                            </div>
                                            <div
                                                v-if="att.created_at || creatorName(att)"
                                                class="proc-tile__upload"
                                            >
                                                <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="proc-tile__upload-icon" />
                                                <span v-if="creatorName(att)">{{ creatorName(att) }}</span>
                                                <span v-if="att.created_at && creatorName(att)" class="proc-tile__upload-dot">·</span>
                                                <span v-if="att.created_at">{{ formatMetaDateTime(att.created_at) }}</span>
                                            </div>
                                        </div>
                                    </button>
                                    <div
                                        v-if="canDownloadAttachment(att) || canUpdateAttachment(att) || canDeleteAttachment(att)"
                                        class="proc-tile__actions"
                                        :class="{ 'proc-tile__actions--open': attachmentMenuOpenId === att.id }"
                                    >
                                        <div class="proc-menu proc-tile-menu">
                                            <button
                                                type="button"
                                                class="proc-tile-menu__trigger"
                                                :class="{ 'proc-tile-menu__trigger--open': attachmentMenuOpenId === att.id }"
                                                aria-label="Attachment actions"
                                                @click.stop="toggleAttachmentMenu(att.id)"
                                            >
                                                <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
                                            </button>
                                            <div
                                                v-if="attachmentMenuOpenId === att.id"
                                                class="proc-menu__dropdown proc-tile-menu__dropdown"
                                            >
                                                <button
                                                    v-if="canDownloadAttachment(att)"
                                                    type="button"
                                                    class="proc-menu__item"
                                                    :disabled="downloading"
                                                    @click.stop="onAttachmentMenuDownload(att)"
                                                >
                                                    <font-awesome-icon
                                                        :icon="['fas', downloading ? 'spinner' : 'download']"
                                                        :spin="downloading"
                                                        class="proc-menu__item-icon"
                                                    />
                                                    {{ downloading ? 'Downloading…' : 'Download' }}
                                                </button>
                                                <button
                                                    v-if="canUpdateAttachment(att)"
                                                    type="button"
                                                    class="proc-menu__item"
                                                    @click.stop="onAttachmentMenuEdit(att)"
                                                >
                                                    <font-awesome-icon :icon="['fas', 'pen']" class="proc-menu__item-icon" />
                                                    Edit label
                                                </button>
                                                <button
                                                    v-if="canDeleteAttachment(att)"
                                                    type="button"
                                                    class="proc-menu__item proc-menu__item--danger"
                                                    :disabled="deletingAttachmentId === att.id"
                                                    @click.stop="onAttachmentMenuDelete(att)"
                                                >
                                                    <font-awesome-icon :icon="['fas', 'trash']" class="proc-menu__item-icon" />
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    v-if="canUploadToItem(selectedItem)"
                                    type="button"
                                    class="proc-tile proc-tile--add"
                                    @click="openUpload(selectedItem)"
                                >
                                    <div class="proc-tile__media proc-tile__media--add">
                                        <font-awesome-icon :icon="['fas', 'plus']" class="proc-tile__add-icon" />
                                    </div>
                                    <div class="proc-tile__caption">
                                        <div class="proc-tile__label">Upload files</div>
                                        <div class="proc-tile__meta">PDF or image</div>
                                    </div>
                                </button>
                            </div>
                        </template>

                        <div v-else class="proc-stage__empty">
                            Select a process item to view its attachments.
                        </div>
                    </section>
                </div>
            </div>
        </div>

        <!-- Create / Edit -->
        <transition name="proc-fade">
            <base-modal v-if="formOpen" fixed-footer width="640px" height="90vh" @close="closeForm">
                <template #header>
                    <div class="proc-form-modal-header">
                        <div class="proc-form-modal-header-inner">
                            <div class="proc-form-modal-header-icon-wrap">
                                <font-awesome-icon
                                    :icon="editingId ? ['fas', 'pen'] : ['fas', 'project-diagram']"
                                    class="proc-form-modal-header-icon"
                                />
                            </div>
                            <div class="text-left">
                                <h2 class="proc-form-modal-header-title">
                                    {{ editingId ? 'Edit Process Item' : 'Add Process Item' }}
                                </h2>
                                <p class="proc-form-modal-header-sub">
                                    {{
                                        editingId
                                            ? 'Update the collection name, description, or attachments'
                                            : 'Create a new process collection for ' + campusTitle
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="proc-form-modal-shell">
                    <div class="proc-form-modal-scroll">
                        <div class="proc-form-modal-card">
                            <div class="proc-form-modal-card__head">
                                <h3 class="proc-form-modal-card__title">Details</h3>
                                <p class="proc-form-modal-card__sub">Basic information for this process collection</p>
                            </div>
                            <div class="proc-form-modal-fields">
                                <div class="proc-form-field">
                                    <label class="proc-form-label">
                                        Name <span class="proc-form-required">*</span>
                                    </label>
                                    <input
                                        v-model="form.title"
                                        type="text"
                                        maxlength="255"
                                        class="proc-form-input"
                                        placeholder="e.g. Student Service Forms, Workflow Library..."
                                    />
                                </div>
                                <div class="proc-form-field">
                                    <label class="proc-form-label">Description</label>
                                    <textarea
                                        v-model="form.description"
                                        rows="3"
                                        class="proc-form-input proc-form-textarea"
                                        placeholder="Optional short summary for this collection"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <div class="proc-form-modal-card proc-form-modal-card--attachments">
                            <div class="proc-form-modal-card__head proc-form-modal-card__head--row">
                                <div>
                                    <h3 class="proc-form-modal-card__title">
                                        Attachments
                                        <span class="proc-form-modal-card__optional">(optional)</span>
                                    </h3>
                                    <p class="proc-form-modal-card__sub">
                                        PDF or image files. Each file needs a label. You can also upload later.
                                    </p>
                                </div>
                                <button type="button" class="proc-form-modal-add-btn" @click="addAttachmentRow">
                                    <font-awesome-icon :icon="['fas', 'plus']" />
                                    Add file
                                </button>
                            </div>

                            <div v-if="!form.attachments.length" class="proc-form-att-empty">
                                <div class="proc-form-att-empty__icon">
                                    <font-awesome-icon :icon="['fas', 'file-upload']" />
                                </div>
                                <p class="proc-form-att-empty__title">No files added yet</p>
                                <p class="proc-form-att-empty__text">Add PDF or image attachments now, or skip and upload later.</p>
                            </div>

                            <div v-else class="proc-form-att-list">
                                <div
                                    v-for="(row, index) in form.attachments"
                                    :key="row.key"
                                    class="proc-form-att-row"
                                >
                                    <div class="proc-form-att-row__preview">
                                        <img v-if="row.previewUrl" :src="row.previewUrl" alt="" class="proc-form-att-row__img" />
                                        <font-awesome-icon
                                            v-else-if="row.existing && row.existing.is_pdf"
                                            :icon="['fas', 'file-pdf']"
                                            class="proc-form-att-row__pdf-icon"
                                        />
                                        <font-awesome-icon
                                            v-else
                                            :icon="['fas', 'file-image']"
                                            class="proc-form-att-row__file-icon"
                                        />
                                    </div>
                                    <div class="proc-form-att-row__fields">
                                        <label class="proc-form-label">
                                            Label <span class="proc-form-required">*</span>
                                        </label>
                                        <input
                                            v-model="row.label"
                                            type="text"
                                            maxlength="255"
                                            class="proc-form-input"
                                            placeholder="Attachment label"
                                        />
                                        <label class="proc-form-file-pick">
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                                class="hidden"
                                                @change="onFilePicked(row, $event)"
                                            />
                                            <font-awesome-icon :icon="['fas', 'paperclip']" class="proc-form-file-pick__icon" />
                                            <span class="proc-form-file-pick__text">
                                                {{
                                                    row.file
                                                        ? row.file.name
                                                        : row.existing
                                                        ? 'Replace file (optional)'
                                                        : 'Choose PDF or image'
                                                }}
                                            </span>
                                        </label>
                                        <p v-if="row.error" class="proc-form-error">{{ row.error }}</p>
                                    </div>
                                    <button
                                        type="button"
                                        class="proc-form-att-row__remove"
                                        title="Remove attachment"
                                        @click="removeAttachmentRow(index)"
                                    >
                                        <font-awesome-icon :icon="['fas', 'trash']" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <p class="proc-form-modal-hint">
                            <font-awesome-icon :icon="['fas', 'lightbulb']" class="proc-form-modal-hint-icon" />
                            Name is required. Attachments are optional when creating — you can upload files anytime from the item gallery.
                        </p>
                    </div>

                    <div class="proc-form-modal-footer">
                        <div class="proc-form-modal-actions">
                            <button
                                type="button"
                                class="proc-form-modal-btn proc-form-modal-btn-secondary"
                                :disabled="saving"
                                @click="closeForm"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                class="proc-form-modal-btn proc-form-modal-btn-primary"
                                :disabled="saving || !isFormValid"
                                @click="submitForm"
                            >
                                <font-awesome-icon v-if="saving" :icon="['fas', 'spinner']" spin class="mr-2" />
                                <font-awesome-icon v-else :icon="['fas', 'check']" class="mr-2" />
                                {{
                                    saving
                                        ? 'Saving…'
                                        : editingId
                                        ? 'Update Process Item'
                                        : 'Save Process Item'
                                }}
                            </button>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>

        <!-- Upload files only -->
        <transition name="proc-fade">
            <base-modal v-if="uploadOpen" fixed-footer width="640px" height="85vh" @close="closeUpload">
                <template #header>
                    <div class="proc-form-modal-header">
                        <div class="proc-form-modal-header-inner">
                            <div class="proc-form-modal-header-icon-wrap">
                                <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="proc-form-modal-header-icon" />
                            </div>
                            <div class="text-left min-w-0">
                                <h2 class="proc-form-modal-header-title">Upload Files</h2>
                                <p class="proc-form-modal-header-sub">
                                    {{
                                        uploadItemTitle
                                            ? 'Add attachments to "' + uploadItemTitle + '"'
                                            : 'Add PDF or image attachments to this process item'
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="proc-form-modal-shell">
                    <div class="proc-form-modal-scroll">
                        <div class="proc-form-modal-card">
                            <div class="proc-form-modal-card__head proc-form-modal-card__head--row">
                                <div>
                                    <h3 class="proc-form-modal-card__title">
                                        Attachments <span class="proc-form-required">*</span>
                                    </h3>
                                    <p class="proc-form-modal-card__sub">
                                        PDF or image (JPG, PNG). Each file needs a label.
                                    </p>
                                </div>
                                <button type="button" class="proc-form-modal-add-btn" @click="addUploadRow">
                                    <font-awesome-icon :icon="['fas', 'plus']" />
                                    Add file
                                </button>
                            </div>

                            <div class="proc-form-att-list">
                                <div
                                    v-for="(row, index) in uploadRows"
                                    :key="row.key"
                                    class="proc-form-att-row"
                                >
                                    <div class="proc-form-att-row__preview">
                                        <img v-if="row.previewUrl" :src="row.previewUrl" alt="" class="proc-form-att-row__img" />
                                        <font-awesome-icon
                                            v-else
                                            :icon="['fas', 'file-upload']"
                                            class="proc-form-att-row__file-icon"
                                        />
                                    </div>
                                    <div class="proc-form-att-row__fields">
                                        <label class="proc-form-label">
                                            Label <span class="proc-form-required">*</span>
                                        </label>
                                        <input
                                            v-model="row.label"
                                            type="text"
                                            maxlength="255"
                                            class="proc-form-input"
                                            placeholder="Attachment label"
                                        />
                                        <label class="proc-form-file-pick">
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                                class="hidden"
                                                @change="onFilePicked(row, $event)"
                                            />
                                            <font-awesome-icon :icon="['fas', 'paperclip']" class="proc-form-file-pick__icon" />
                                            <span class="proc-form-file-pick__text">
                                                {{ row.file ? row.file.name : 'Choose PDF or image' }}
                                            </span>
                                        </label>
                                        <p v-if="row.error" class="proc-form-error">{{ row.error }}</p>
                                    </div>
                                    <button
                                        type="button"
                                        class="proc-form-att-row__remove"
                                        title="Remove row"
                                        :disabled="uploadRows.length <= 1"
                                        @click="removeUploadRow(index)"
                                    >
                                        <font-awesome-icon :icon="['fas', 'trash']" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <p class="proc-form-modal-hint">
                            <font-awesome-icon :icon="['fas', 'lightbulb']" class="proc-form-modal-hint-icon" />
                            Each attachment must have a label and a file. Use “Add file” to upload multiple files at once.
                        </p>
                    </div>

                    <div class="proc-form-modal-footer">
                        <div class="proc-form-modal-actions">
                            <button
                                type="button"
                                class="proc-form-modal-btn proc-form-modal-btn-secondary"
                                :disabled="uploading"
                                @click="closeUpload"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                class="proc-form-modal-btn proc-form-modal-btn-primary"
                                :disabled="uploading || !isUploadValid"
                                @click="submitUpload"
                            >
                                <font-awesome-icon v-if="uploading" :icon="['fas', 'spinner']" spin class="mr-2" />
                                <font-awesome-icon v-else :icon="['fas', 'upload']" class="mr-2" />
                                {{ uploading ? 'Uploading…' : 'Upload Files' }}
                            </button>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>

        <!-- Edit attachment label -->
        <transition name="proc-fade">
            <base-modal v-if="labelEditOpen" fixed-footer width="520px" @close="closeEditLabel">
                <template #header>
                    <div class="proc-form-modal-header">
                        <div class="proc-form-modal-header-inner">
                            <div class="proc-form-modal-header-icon-wrap">
                                <font-awesome-icon :icon="['fas', 'pen']" class="proc-form-modal-header-icon" />
                            </div>
                            <div class="text-left min-w-0">
                                <h2 class="proc-form-modal-header-title">Edit Attachment Label</h2>
                                <p class="proc-form-modal-header-sub">
                                    Update the display label for this attachment
                                </p>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="proc-form-modal-shell">
                    <div class="proc-form-modal-scroll proc-form-modal-scroll--compact">
                        <div class="proc-form-modal-card">
                            <div class="proc-form-modal-card__head">
                                <h3 class="proc-form-modal-card__title">Label</h3>
                                <p class="proc-form-modal-card__sub">
                                    This name is shown on the attachment tile and in previews.
                                </p>
                            </div>
                            <div class="proc-form-modal-fields">
                                <div class="proc-form-field">
                                    <label class="proc-form-label">
                                        Attachment label <span class="proc-form-required">*</span>
                                    </label>
                                    <input
                                        ref="labelEditInput"
                                        v-model="labelEditValue"
                                        type="text"
                                        maxlength="255"
                                        class="proc-form-input"
                                        placeholder="Enter attachment label"
                                        @keydown.enter.prevent="submitEditLabel"
                                    />
                                    <p v-if="labelEditFilename" class="proc-form-filename-hint">
                                        <font-awesome-icon :icon="['fas', 'file']" class="proc-form-filename-hint__icon" />
                                        {{ labelEditFilename }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="proc-form-modal-footer">
                        <div class="proc-form-modal-actions">
                            <button
                                type="button"
                                class="proc-form-modal-btn proc-form-modal-btn-secondary"
                                :disabled="labelEditSaving"
                                @click="closeEditLabel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                class="proc-form-modal-btn proc-form-modal-btn-primary"
                                :disabled="labelEditSaving || !isLabelEditValid"
                                @click="submitEditLabel"
                            >
                                <font-awesome-icon v-if="labelEditSaving" :icon="['fas', 'spinner']" spin class="mr-2" />
                                <font-awesome-icon v-else :icon="['fas', 'check']" class="mr-2" />
                                {{ labelEditSaving ? 'Saving…' : 'Save Label' }}
                            </button>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>

        <!-- Preview -->
        <transition name="proc-fade">
            <base-modal
                v-if="previewAttachment"
                fixed-footer
                :fullscreen="previewMaximized"
                :zoom-animation="previewZoomAnim"
                width="960px"
                height="85vh"
                @close="closePreview"
            >
                <template #headerActions>
                    <button
                        type="button"
                        class="base-modal-control-btn base-modal-control-btn--expand"
                        :aria-label="previewMaximized ? 'Exit full screen' : 'Full screen'"
                        @click="togglePreviewMaximize"
                    >
                        <font-awesome-icon :icon="['fas', previewMaximized ? 'compress' : 'expand']" />
                    </button>
                </template>
                <template #header>
                    <div
                        class="proc-form-modal-header proc-form-modal-header--with-controls"
                        :class="{ 'proc-form-modal-header--fullscreen': previewMaximized }"
                    >
                        <div class="proc-form-modal-header-inner">
                            <div class="proc-form-modal-header-icon-wrap">
                                <font-awesome-icon
                                    :icon="previewAttachment.is_pdf ? ['fas', 'file-pdf'] : ['fas', 'search-plus']"
                                    class="proc-form-modal-header-icon"
                                />
                            </div>
                            <div class="text-left min-w-0">
                                <h2 class="proc-form-modal-header-title">{{ previewAttachment.label }}</h2>
                                <p class="proc-form-modal-header-sub">{{ previewAttachment.original_filename }}</p>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="proc-form-modal-shell">
                    <div class="proc-preview-modal-scroll" :class="{ 'proc-preview-modal-scroll--maximized': previewMaximized }">
                        <div v-if="!previewMaximized" class="proc-preview-modal-meta">
                            <div class="proc-meta-chip">
                                <span class="proc-meta-chip__icon-wrap proc-meta-chip__icon-wrap--type">
                                    <font-awesome-icon
                                        :icon="previewAttachment.is_pdf ? ['fas', 'file-pdf'] : ['fas', 'image']"
                                    />
                                </span>
                                <span class="proc-meta-chip__text">
                                    {{ previewAttachment.is_pdf ? 'PDF document' : 'Image' }}
                                </span>
                            </div>
                            <div v-if="previewAttachment.formatted_file_size" class="proc-meta-chip">
                                <span class="proc-meta-chip__icon-wrap proc-meta-chip__icon-wrap--size">
                                    <font-awesome-icon :icon="['fas', 'database']" />
                                </span>
                                <span class="proc-meta-chip__text">{{ previewAttachment.formatted_file_size }}</span>
                            </div>
                            <div v-if="creatorName(previewAttachment)" class="proc-meta-chip">
                                <span class="proc-meta-chip__icon-wrap">
                                    <font-awesome-icon :icon="['fas', 'user']" />
                                </span>
                                <span class="proc-meta-chip__text">
                                    Uploaded by <strong>{{ creatorName(previewAttachment) }}</strong>
                                </span>
                            </div>
                            <div v-if="previewAttachment.created_at" class="proc-meta-chip">
                                <span class="proc-meta-chip__icon-wrap proc-meta-chip__icon-wrap--time">
                                    <font-awesome-icon :icon="['fas', 'clock']" />
                                </span>
                                <span class="proc-meta-chip__text">{{ formatMetaDateTime(previewAttachment.created_at) }}</span>
                            </div>
                        </div>

                        <div
                            class="proc-preview-modal-frame"
                            :class="{
                                'proc-preview-modal-frame--image': previewAttachment.is_image,
                                'proc-preview-modal-frame--pdf': previewAttachment.is_pdf,
                            }"
                        >
                            <div v-if="previewLoading" class="proc-preview-modal-state">
                                <font-awesome-icon :icon="['fas', 'spinner']" spin class="proc-preview-modal-state__icon" />
                                <span class="proc-preview-modal-state__text">Loading preview…</span>
                            </div>
                            <img
                                v-else-if="previewAttachment.is_image && previewDisplayUrl"
                                :src="previewDisplayUrl"
                                :alt="previewAttachment.label"
                                class="proc-preview-modal-img"
                            />
                            <iframe
                                v-else-if="previewAttachment.is_pdf && previewDisplayUrl"
                                :src="previewDisplayUrl"
                                class="proc-preview-modal-iframe"
                                title="PDF preview"
                            />
                            <div
                                v-else-if="previewAttachment.is_image || previewAttachment.is_pdf"
                                class="proc-preview-modal-state"
                            >
                                <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="proc-preview-modal-state__icon proc-preview-modal-state__icon--warn" />
                                <span class="proc-preview-modal-state__text">Preview unavailable.</span>
                            </div>
                        </div>
                    </div>

                    <div class="proc-form-modal-footer" :class="{ 'proc-form-modal-footer--fullscreen': previewMaximized }">
                        <div class="proc-form-modal-actions">
                            <button
                                type="button"
                                class="proc-form-modal-btn proc-form-modal-btn-secondary"
                                @click="closePreview"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                class="proc-form-modal-btn proc-form-modal-btn-primary"
                                :disabled="downloading"
                                @click="downloadAttachment(previewAttachment)"
                            >
                                <font-awesome-icon v-if="downloading" :icon="['fas', 'spinner']" spin class="mr-2" />
                                <font-awesome-icon v-else :icon="['fas', 'download']" class="mr-2" />
                                {{ downloading ? 'Downloading…' : 'Download' }}
                            </button>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import BreadCrumb from "../../../components/shared/BreadCrumb.vue";
import Pagination from "../../../components/shared/pagination.vue";
import { ADPOLY_ENTITY, getCampusConfig, isValidCampus } from "../config";

const LIST_QUERY = gql`
    query ProcessItems(
        $entity: String
        $campus: String
        $search: String
        $first: Int
        $page: Int
    ) {
        processItems(
            entity: $entity
            campus: $campus
            search: $search
            first: $first
            page: $page
        ) {
            error
            message
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
            data {
                id
                entity
                campus
                title
                description
                sort_order
                created_by
                created_at
                createdBy {
                    id
                    name
                }
                attachments {
                    id
                    label
                    original_filename
                    file_type
                    file_extension
                    file_size
                    formatted_file_size
                    is_image
                    is_pdf
                    sort_order
                    created_by
                    created_at
                    createdBy {
                        id
                        name
                    }
                }
            }
        }
    }
`;

const OVERVIEW_QUERY = gql`
    query ProcessOverview($entity: String!, $campus: String!) {
        processOverview(entity: $entity, campus: $campus) {
            error
            message
            data {
                items_count
                attachments_count
            }
        }
    }
`;

const CREATE_MUTATION = gql`
    mutation CreateProcessItem($input: CreateProcessItemInput!) {
        createProcessItem(input: $input) {
            error
            message
            data {
                id
                title
            }
        }
    }
`;

const UPDATE_MUTATION = gql`
    mutation UpdateProcessItem($id: ID!, $input: UpdateProcessItemInput!) {
        updateProcessItem(id: $id, input: $input) {
            error
            message
            data {
                id
                title
            }
        }
    }
`;

const DELETE_MUTATION = gql`
    mutation DeleteProcessItem($id: ID!) {
        deleteProcessItem(id: $id) {
            error
            message
        }
    }
`;

const DELETE_ATTACHMENT_MUTATION = gql`
    mutation DeleteProcessItemAttachment($id: ID!) {
        deleteProcessItemAttachment(id: $id) {
            error
            message
        }
    }
`;

const UPDATE_ATTACHMENT_MUTATION = gql`
    mutation UpdateProcessItemAttachment($id: ID!, $input: UpdateProcessItemAttachmentInput!) {
        updateProcessItemAttachment(id: $id, input: $input) {
            error
            message
            data {
                id
                label
            }
        }
    }
`;

const ADD_ATTACHMENTS_MUTATION = gql`
    mutation AddProcessItemAttachments($id: ID!, $input: AddProcessItemAttachmentsInput!) {
        addProcessItemAttachments(id: $id, input: $input) {
            error
            message
            data {
                id
                title
            }
        }
    }
`;

var ATTACHMENT_KEY = 1;

function newAttachmentRow(existing) {
    ATTACHMENT_KEY += 1;
    return {
        key: "att-" + ATTACHMENT_KEY,
        label: existing && existing.label ? existing.label : "",
        file: null,
        previewUrl: null,
        existing: existing || null,
        error: null,
    };
}

export default {
    name: "adpoly-process",
    components: {
        BreadCrumb: BreadCrumb,
        Pagination: Pagination,
    },
    props: ["campus"],
    data() {
        return {
            entity: ADPOLY_ENTITY,
            pageLoading: false,
            items: [],
            paginatorInfo: null,
            page: 1,
            first: 10,
            selectedId: null,
            searchText: "",
            searchDebounce: null,
            overview: {
                items_count: 0,
                attachments_count: 0,
            },
            formOpen: false,
            editingId: null,
            saving: false,
            form: {
                title: "",
                description: "",
                attachments: [],
            },
            uploadOpen: false,
            uploadItemId: null,
            uploadItemTitle: "",
            uploadRows: [],
            uploading: false,
            previewAttachment: null,
            previewDisplayUrl: null,
            previewLoading: false,
            previewMaximized: false,
            previewZoomAnim: '',
            previewZoomTimer: null,
            attachmentBlobUrls: {},
            attachmentThumbLoadingIds: {},
            downloading: false,
            downloadingAll: false,
            deletingAttachmentId: null,
            labelEditOpen: false,
            labelEditId: null,
            labelEditValue: "",
            labelEditFilename: "",
            labelEditSaving: false,
            actionsMenuOpen: false,
            attachmentMenuOpenId: null,
        };
    },
    created() {
        this.validateCampus();
        this.loadOverview();
        this.loadItems();
    },
    mounted() {
        document.addEventListener("click", this.onDocumentClick);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.onDocumentClick);
        this.clearSearchDebounce();
        this.revokePreviewUrls();
        this.revokeUploadPreviewUrls();
        this.revokeAttachmentBlobUrls();
        this.clearPreviewZoomTimer();
    },
    watch: {
        campus: function () {
            this.validateCampus();
            this.selectedId = null;
            this.actionsMenuOpen = false;
            this.page = 1;
            this.searchText = "";
            this.loadOverview();
            this.loadItems();
        },
        selectedId: function () {
            this.actionsMenuOpen = false;
            this.attachmentMenuOpenId = null;
            this.loadSelectedItemThumbnails();
        },
        items: function (list) {
            if (!list || !list.length) {
                this.selectedId = null;
                return;
            }
            var stillSelected = false;
            for (var i = 0; i < list.length; i++) {
                if (list[i].id === this.selectedId) {
                    stillSelected = true;
                    break;
                }
            }
            if (!stillSelected) {
                this.selectedId = list[0].id;
            }
        },
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions || "";
        },
        userId() {
            var uid = this.$store.getters.userId;
            if (!uid) return null;
            return parseInt(uid, 10);
        },
        permissionsArray() {
            var permissions = this.permissions;
            if (!permissions) return [];
            if (Array.isArray(permissions)) return permissions;
            return String(permissions).split(",");
        },
        campusConfig() {
            return getCampusConfig(this.campus);
        },
        campusTitle() {
            return this.campusConfig ? this.campusConfig.title : "";
        },
        campusCode() {
            return this.campusConfig ? this.campusConfig.campusCode : "";
        },
        breadCrumbItems() {
            var campusKey = this.campusConfig ? this.campusConfig.key : "";
            return [
                { label: "Dashboard", path: "/dashboard" },
                { label: "ADPOLY", path: "/adpoly" },
                {
                    label: this.campusTitle || "Campus",
                    path: campusKey ? "/adpoly/" + campusKey : "/adpoly",
                },
                {
                    label: "Campus",
                    path: campusKey ? "/adpoly/" + campusKey + "/campus" : "/adpoly",
                },
                { label: "Process" },
            ];
        },
        canCreate() {
            return this.hasProcessPermission("process-items-create");
        },
        isLabelEditValid() {
            return !!(this.labelEditValue && String(this.labelEditValue).trim());
        },
        showEmptyState() {
            var hasSearch = !!(this.searchText && String(this.searchText).trim());
            if (hasSearch) return false;
            if (this.paginatorInfo && this.paginatorInfo.total > 0) return false;
            return !this.items.length;
        },
        paginatorInfoWithItems() {
            return this.paginatorInfo || {
                currentPage: 1,
                lastPage: 1,
                perPage: this.first,
                total: 0,
                hasMorePages: false,
                firstItem: 0,
                lastItem: 0,
            };
        },
        selectedItem() {
            if (!this.selectedId) return null;
            for (var i = 0; i < this.items.length; i++) {
                if (this.items[i].id === this.selectedId) return this.items[i];
            }
            return null;
        },
        isFormValid() {
            if (!this.form.title || !String(this.form.title).trim()) return false;
            var rows = this.form.attachments || [];
            for (var i = 0; i < rows.length; i++) {
                var row = rows[i];
                if (row.error) return false;
                var hasFile = !!(row.file || row.existing);
                var hasLabel = !!(row.label && String(row.label).trim());
                if (!hasFile && !hasLabel) continue;
                if (!hasLabel || !hasFile) return false;
            }
            return true;
        },
        isUploadValid() {
            var rows = this.uploadRows || [];
            if (!rows.length) return false;
            for (var i = 0; i < rows.length; i++) {
                var row = rows[i];
                if (row.error) return false;
                if (!row.label || !String(row.label).trim()) return false;
                if (!row.file) return false;
            }
            return true;
        },
    },
    methods: {
        validateCampus() {
            if (!isValidCampus(this.campus)) {
                this.$router.push({ path: "/notFound" });
            }
        },
        hasProcessPermission(name) {
            return this.permissionsArray.indexOf(name) !== -1;
        },
        isProcessOwner(creatorId) {
            if (this.role === "superadmin") return true;
            if (!creatorId || !this.userId) return false;
            return parseInt(creatorId, 10) === parseInt(this.userId, 10);
        },
        canUpdateItem(item) {
            if (!item) return false;
            if (!this.hasProcessPermission("process-items-update")) return false;
            return this.isProcessOwner(item.created_by);
        },
        canDeleteItem(item) {
            if (!item) return false;
            if (!this.hasProcessPermission("process-items-delete")) return false;
            return this.isProcessOwner(item.created_by);
        },
        canUpdateAttachment(att) {
            if (!att) return false;
            if (!this.hasProcessPermission("process-items-update")) return false;
            return this.isProcessOwner(att.created_by);
        },
        canDeleteAttachment(att) {
            if (!att) return false;
            if (!this.hasProcessPermission("process-items-delete")) return false;
            return this.isProcessOwner(att.created_by);
        },
        canDownloadAttachment(att) {
            if (!att || !att.id) return false;
            if (this.role === "superadmin") return true;
            return (
                this.hasProcessPermission("process-items-index") ||
                this.hasProcessPermission("adpoly-process-index")
            );
        },
        canDownloadProcessItem(item) {
            if (!item || !item.attachments || item.attachments.length < 2) return false;
            if (this.role === "superadmin") return true;
            return (
                this.hasProcessPermission("process-items-index") ||
                this.hasProcessPermission("adpoly-process-index")
            );
        },
        canUploadToItem(item) {
            if (!item) return false;
            return (
                this.hasProcessPermission("process-items-update") ||
                this.hasProcessPermission("process-items-create")
            );
        },
        formatNumber(value) {
            var n = Number(value);
            if (isNaN(n)) return "0";
            return n.toLocaleString();
        },
        creatorName(entity) {
            if (entity && entity.createdBy && entity.createdBy.name) {
                return entity.createdBy.name;
            }
            if (entity && entity.created_by) {
                return "Unknown user";
            }
            return "";
        },
        formatMetaDate(value) {
            if (!value) return "";
            var date = new Date(value);
            if (isNaN(date.getTime())) return "";
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        },
        formatMetaDateTime(value) {
            if (!value) return "";
            var date = new Date(value);
            if (isNaN(date.getTime())) return "";
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
        },
        attachmentCount(item) {
            return item && item.attachments ? item.attachments.length : 0;
        },
        selectItem(id) {
            this.selectedId = id;
            this.actionsMenuOpen = false;
            this.attachmentMenuOpenId = null;
        },
        toggleActionsMenu() {
            this.actionsMenuOpen = !this.actionsMenuOpen;
            this.attachmentMenuOpenId = null;
        },
        closeActionsMenu() {
            this.actionsMenuOpen = false;
        },
        toggleAttachmentMenu(id) {
            if (this.attachmentMenuOpenId === id) {
                this.attachmentMenuOpenId = null;
                return;
            }
            this.attachmentMenuOpenId = id;
            this.actionsMenuOpen = false;
        },
        closeAttachmentMenu() {
            this.attachmentMenuOpenId = null;
        },
        onAttachmentMenuEdit(att) {
            this.closeAttachmentMenu();
            this.openEditLabel(att);
        },
        onAttachmentMenuDownload(att) {
            this.closeAttachmentMenu();
            this.downloadAttachment(att);
        },
        onAttachmentMenuDelete(att) {
            this.closeAttachmentMenu();
            this.confirmDeleteAttachment(att);
        },
        onDocumentClick(event) {
            if (this.actionsMenuOpen) {
                var menu = this.$refs.actionsMenu;
                if (!(menu && event && event.target && menu.contains(event.target))) {
                    this.actionsMenuOpen = false;
                }
            }
            if (this.attachmentMenuOpenId) {
                var target = event && event.target;
                var insideAttachmentMenu =
                    target && target.closest && target.closest(".proc-tile-menu");
                if (!insideAttachmentMenu) {
                    this.attachmentMenuOpenId = null;
                }
            }
        },
        onMenuEdit() {
            this.actionsMenuOpen = false;
            if (this.selectedItem) this.openEdit(this.selectedItem);
        },
        onMenuDownloadAll() {
            this.actionsMenuOpen = false;
            if (this.selectedItem) this.downloadAllAttachments(this.selectedItem);
        },
        onMenuDelete() {
            this.actionsMenuOpen = false;
            if (this.selectedItem) this.confirmDelete(this.selectedItem);
        },
        clearSearchDebounce() {
            if (this.searchDebounce) {
                clearTimeout(this.searchDebounce);
                this.searchDebounce = null;
            }
        },
        onSearchInput() {
            var self = this;
            this.clearSearchDebounce();
            this.searchDebounce = setTimeout(function () {
                self.page = 1;
                self.loadItems();
            }, 350);
        },
        setPage(page) {
            this.page = page;
            this.loadItems();
        },
        loadOverview() {
            if (!this.campusCode) return;
            var self = this;
            this.$apollo
                .query({
                    query: OVERVIEW_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campusCode,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (res) {
                    var payload =
                        res && res.data && res.data.processOverview ? res.data.processOverview : null;
                    if (payload && !payload.error && payload.data) {
                        self.overview = {
                            items_count: payload.data.items_count || 0,
                            attachments_count: payload.data.attachments_count || 0,
                        };
                    }
                })
                .catch(function () {
                    /* keep defaults */
                });
        },
        loadItems() {
            if (!this.campusCode) return;
            var self = this;
            this.pageLoading = true;
            this.$apollo
                .query({
                    query: LIST_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campusCode,
                        search: this.searchText ? String(this.searchText).trim() : null,
                        first: this.first,
                        page: this.page,
                    },
                    fetchPolicy: "network-only",
                })
                .then(function (res) {
                    var payload = res && res.data && res.data.processItems ? res.data.processItems : null;
                    if (payload && !payload.error) {
                        self.items = payload.data || [];
                        self.paginatorInfo = payload.paginatorInfo || null;
                        if (
                            self.paginatorInfo &&
                            self.paginatorInfo.lastPage > 0 &&
                            self.page > self.paginatorInfo.lastPage
                        ) {
                            self.page = self.paginatorInfo.lastPage;
                            self.loadItems();
                            return;
                        }
                        self.loadSelectedItemThumbnails();
                    } else if (payload && payload.error) {
                        self.toastError(payload.message || "Failed to load process items.");
                    }
                })
                .catch(function () {
                    self.toastError("Failed to load process items.");
                })
                .finally(function () {
                    self.pageLoading = false;
                });
        },
        refreshLists() {
            this.loadOverview();
            this.loadItems();
        },
        openCreate() {
            if (!this.canCreate) return;
            this.editingId = null;
            this.revokePreviewUrls();
            this.form = {
                title: "",
                description: "",
                attachments: [],
            };
            this.formOpen = true;
        },
        openEdit(item) {
            if (!item || !this.canUpdateItem(item)) return;
            this.editingId = item.id;
            this.revokePreviewUrls();
            var rows = [];
            var attachments = item.attachments || [];
            for (var i = 0; i < attachments.length; i++) {
                rows.push(newAttachmentRow(attachments[i]));
            }
            this.form = {
                title: item.title || "",
                description: item.description || "",
                attachments: rows,
            };
            this.formOpen = true;
            this.hydrateFormExistingPreviews();
        },
        closeForm() {
            this.formOpen = false;
            this.editingId = null;
            this.revokePreviewUrls();
        },
        openUpload(item) {
            if (!item || !this.canUploadToItem(item)) return;
            this.uploadItemId = item.id;
            this.uploadItemTitle = item.title || "";
            this.revokeUploadPreviewUrls();
            this.uploadRows = [newAttachmentRow(null)];
            this.uploadOpen = true;
        },
        closeUpload() {
            this.uploadOpen = false;
            this.uploadItemId = null;
            this.uploadItemTitle = "";
            this.revokeUploadPreviewUrls();
            this.uploadRows = [];
        },
        addAttachmentRow() {
            this.form.attachments.push(newAttachmentRow(null));
        },
        removeAttachmentRow(index) {
            var row = this.form.attachments[index];
            if (row && row.previewUrl && row.file) {
                try {
                    URL.revokeObjectURL(row.previewUrl);
                } catch (e) {
                    /* ignore */
                }
            }
            this.form.attachments.splice(index, 1);
        },
        addUploadRow() {
            this.uploadRows.push(newAttachmentRow(null));
        },
        removeUploadRow(index) {
            if (this.uploadRows.length <= 1) return;
            var row = this.uploadRows[index];
            if (row && row.previewUrl && row.file) {
                try {
                    URL.revokeObjectURL(row.previewUrl);
                } catch (e) {
                    /* ignore */
                }
            }
            this.uploadRows.splice(index, 1);
        },
        revokePreviewUrls() {
            var rows = this.form && this.form.attachments ? this.form.attachments : [];
            for (var i = 0; i < rows.length; i++) {
                if (rows[i].file && rows[i].previewUrl) {
                    try {
                        URL.revokeObjectURL(rows[i].previewUrl);
                    } catch (e) {
                        /* ignore */
                    }
                }
            }
        },
        revokeUploadPreviewUrls() {
            var rows = this.uploadRows || [];
            for (var i = 0; i < rows.length; i++) {
                if (rows[i].file && rows[i].previewUrl) {
                    try {
                        URL.revokeObjectURL(rows[i].previewUrl);
                    } catch (e) {
                        /* ignore */
                    }
                }
            }
        },
        onFilePicked(row, event) {
            var files = event && event.target && event.target.files ? event.target.files : null;
            var file = files && files.length ? files[0] : null;
            row.error = null;
            if (!file) {
                row.file = null;
                return;
            }

            if (file.size > 20 * 1024 * 1024) {
                row.error = "File must be 20MB or smaller.";
                row.file = null;
                event.target.value = "";
                return;
            }

            var name = String(file.name || "").toLowerCase();
            var okExt =
                (name.length >= 4 && name.substring(name.length - 4) === ".pdf") ||
                (name.length >= 4 && name.substring(name.length - 4) === ".jpg") ||
                (name.length >= 5 && name.substring(name.length - 5) === ".jpeg") ||
                (name.length >= 4 && name.substring(name.length - 4) === ".png");
            var okType =
                file.type === "application/pdf" ||
                file.type === "image/jpeg" ||
                file.type === "image/png";

            if (!okExt && !okType) {
                row.error = "Only PDF, JPG, and PNG files are allowed.";
                row.file = null;
                event.target.value = "";
                return;
            }

            if (row.file && row.previewUrl) {
                try {
                    URL.revokeObjectURL(row.previewUrl);
                } catch (e) {
                    /* ignore */
                }
            }

            row.file = file;
            row.previewUrl = file.type.indexOf("image/") === 0 ? URL.createObjectURL(file) : null;
            if (!row.label || !String(row.label).trim()) {
                row.label = file.name.replace(/\.[^.]+$/, "");
            }
        },
        submitForm() {
            if (!this.isFormValid || this.saving) return;
            if (this.editingId) this.submitUpdate();
            else this.submitCreate();
        },
        submitCreate() {
            var self = this;
            var labels = [];
            var files = [];
            for (var i = 0; i < this.form.attachments.length; i++) {
                var createRow = this.form.attachments[i];
                if (!createRow.file) continue;
                labels.push(String(createRow.label).trim());
                files.push(createRow.file);
            }
            this.saving = true;
            this.$apollo
                .mutate({
                    mutation: CREATE_MUTATION,
                    variables: {
                        input: {
                            entity: this.entity,
                            campus: this.campusCode,
                            title: String(this.form.title).trim(),
                            description: this.form.description ? String(this.form.description).trim() : null,
                            attachment_labels: labels.length ? labels : null,
                            attachment_files: files.length ? files : null,
                        },
                    },
                })
                .then(function (res) {
                    var payload = res && res.data && res.data.createProcessItem ? res.data.createProcessItem : null;
                    if (payload && !payload.error) {
                        self.closeForm();
                        self.selectedId = payload.data && payload.data.id ? payload.data.id : self.selectedId;
                        self.page = 1;
                        self.refreshLists();
                        self.toastSuccess(payload.message || "Process item created.");
                    } else {
                        self.toastError((payload && payload.message) || "Failed to create process item.");
                    }
                })
                .catch(function () {
                    self.toastError("Failed to create process item.");
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        submitUpdate() {
            var self = this;
            var existing = [];
            var newLabels = [];
            var newFiles = [];
            for (var i = 0; i < this.form.attachments.length; i++) {
                var row = this.form.attachments[i];
                var label = row.label ? String(row.label).trim() : "";
                if (row.file) {
                    if (!label) continue;
                    newLabels.push(label);
                    newFiles.push(row.file);
                } else if (row.existing) {
                    existing.push({ id: row.existing.id, label: label || row.existing.label });
                }
            }
            this.saving = true;
            this.$apollo
                .mutate({
                    mutation: UPDATE_MUTATION,
                    variables: {
                        id: this.editingId,
                        input: {
                            title: String(this.form.title).trim(),
                            description: this.form.description ? String(this.form.description).trim() : null,
                            existing_attachments: existing,
                            new_attachment_labels: newLabels,
                            new_attachment_files: newFiles,
                        },
                    },
                })
                .then(function (res) {
                    var payload = res && res.data && res.data.updateProcessItem ? res.data.updateProcessItem : null;
                    if (payload && !payload.error) {
                        self.closeForm();
                        self.refreshLists();
                        self.toastSuccess(payload.message || "Process item updated.");
                    } else {
                        self.toastError((payload && payload.message) || "Failed to update process item.");
                    }
                })
                .catch(function () {
                    self.toastError("Failed to update process item.");
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        submitUpload() {
            if (!this.isUploadValid || this.uploading || !this.uploadItemId) return;
            var self = this;
            var labels = [];
            var files = [];
            for (var i = 0; i < this.uploadRows.length; i++) {
                labels.push(String(this.uploadRows[i].label).trim());
                files.push(this.uploadRows[i].file);
            }
            this.uploading = true;
            this.$apollo
                .mutate({
                    mutation: ADD_ATTACHMENTS_MUTATION,
                    variables: {
                        id: this.uploadItemId,
                        input: {
                            attachment_labels: labels,
                            attachment_files: files,
                        },
                    },
                })
                .then(function (res) {
                    var payload =
                        res && res.data && res.data.addProcessItemAttachments
                            ? res.data.addProcessItemAttachments
                            : null;
                    if (payload && !payload.error) {
                        self.closeUpload();
                        self.refreshLists();
                        self.toastSuccess(payload.message || "Attachments uploaded.");
                    } else {
                        self.toastError((payload && payload.message) || "Failed to upload attachments.");
                    }
                })
                .catch(function () {
                    self.toastError("Failed to upload attachments.");
                })
                .finally(function () {
                    self.uploading = false;
                });
        },
        confirmDelete(item) {
            var self = this;
            if (!item || !this.canDeleteItem(item) || !this.$swal) return;
            this.$swal
                .fire({
                    icon: "warning",
                    title: "Delete process item?",
                    text: 'This will permanently delete "' + item.title + '" and all its attachments.',
                    showCancelButton: true,
                    confirmButtonText: "Delete",
                    confirmButtonColor: "#dc2626",
                })
                .then(function (result) {
                    if (result && result.isConfirmed) self.deleteItem(item.id);
                });
        },
        deleteItem(id) {
            var self = this;
            this.$apollo
                .mutate({
                    mutation: DELETE_MUTATION,
                    variables: { id: id },
                })
                .then(function (res) {
                    var payload = res && res.data && res.data.deleteProcessItem ? res.data.deleteProcessItem : null;
                    if (payload && !payload.error) {
                        if (self.selectedId === id) self.selectedId = null;
                        if (self.items.length <= 1 && self.page > 1) {
                            self.page = self.page - 1;
                        }
                        self.refreshLists();
                        self.toastSuccess(payload.message || "Process item deleted.");
                    } else {
                        self.toastError((payload && payload.message) || "Failed to delete process item.");
                    }
                })
                .catch(function () {
                    self.toastError("Failed to delete process item.");
                });
        },
        openEditLabel(att) {
            var self = this;
            if (!att || !att.id || !this.canUpdateAttachment(att)) return;
            this.labelEditId = att.id;
            this.labelEditValue = att.label || "";
            this.labelEditFilename = att.original_filename || "";
            this.labelEditSaving = false;
            this.labelEditOpen = true;
            this.$nextTick(function () {
                var input = self.$refs.labelEditInput;
                if (input && input.focus) {
                    input.focus();
                    if (input.select) input.select();
                }
            });
        },
        closeEditLabel() {
            if (this.labelEditSaving) return;
            this.labelEditOpen = false;
            this.labelEditId = null;
            this.labelEditValue = "";
            this.labelEditFilename = "";
        },
        submitEditLabel() {
            var self = this;
            if (!this.isLabelEditValid || !this.labelEditId || this.labelEditSaving) return;
            var label = String(this.labelEditValue).trim();
            this.labelEditSaving = true;
            this.$apollo
                .mutate({
                    mutation: UPDATE_ATTACHMENT_MUTATION,
                    variables: {
                        id: this.labelEditId,
                        input: { label: label },
                    },
                })
                .then(function (res) {
                    var payload =
                        res && res.data && res.data.updateProcessItemAttachment
                            ? res.data.updateProcessItemAttachment
                            : null;
                    if (payload && !payload.error) {
                        if (
                            self.previewAttachment &&
                            String(self.previewAttachment.id) === String(self.labelEditId)
                        ) {
                            self.previewAttachment = Object.assign({}, self.previewAttachment, {
                                label: label,
                            });
                        }
                        self.labelEditSaving = false;
                        self.closeEditLabel();
                        self.refreshLists();
                        self.toastSuccess(payload.message || "Attachment label updated.");
                    } else {
                        self.toastError((payload && payload.message) || "Failed to update label.");
                    }
                })
                .catch(function () {
                    self.toastError("Failed to update label.");
                })
                .finally(function () {
                    self.labelEditSaving = false;
                });
        },
        confirmDeleteAttachment(att) {
            var self = this;
            if (!att || !att.id || !this.canDeleteAttachment(att) || !this.$swal) return;
            var label = att.label || att.original_filename || "this attachment";
            this.$swal
                .fire({
                    icon: "warning",
                    title: "Delete attachment?",
                    text: 'This will permanently delete "' + label + '".',
                    showCancelButton: true,
                    confirmButtonText: "Delete",
                    confirmButtonColor: "#dc2626",
                })
                .then(function (result) {
                    if (result && result.isConfirmed) self.deleteAttachment(att.id);
                });
        },
        deleteAttachment(id) {
            var self = this;
            this.deletingAttachmentId = id;
            this.$apollo
                .mutate({
                    mutation: DELETE_ATTACHMENT_MUTATION,
                    variables: { id: id },
                })
                .then(function (res) {
                    var payload =
                        res && res.data && res.data.deleteProcessItemAttachment
                            ? res.data.deleteProcessItemAttachment
                            : null;
                    if (payload && !payload.error) {
                        if (
                            self.previewAttachment &&
                            String(self.previewAttachment.id) === String(id)
                        ) {
                            self.closePreview();
                        }
                        self.refreshLists();
                        self.toastSuccess(payload.message || "Attachment deleted.");
                    } else {
                        self.toastError((payload && payload.message) || "Failed to delete attachment.");
                    }
                })
                .catch(function () {
                    self.toastError("Failed to delete attachment.");
                })
                .finally(function () {
                    self.deletingAttachmentId = null;
                });
        },
        openPreview(att) {
            if (!att) return;
            this.closeAttachmentMenu();
            if (!att.is_image && !att.is_pdf) {
                this.downloadAttachment(att);
                return;
            }
            var self = this;
            this.previewAttachment = att;
            this.previewDisplayUrl = null;
            this.previewLoading = true;
            this.fetchAttachmentBlobUrl(att.id)
                .then(function (url) {
                    if (self.previewAttachment && String(self.previewAttachment.id) === String(att.id)) {
                        self.previewDisplayUrl = url;
                    }
                })
                .catch(function (error) {
                    if (self.previewAttachment && String(self.previewAttachment.id) === String(att.id)) {
                        self.toastError((error && error.message) || "Failed to load preview.");
                    }
                })
                .finally(function () {
                    if (self.previewAttachment && String(self.previewAttachment.id) === String(att.id)) {
                        self.previewLoading = false;
                    }
                });
        },
        closePreview() {
            this.previewAttachment = null;
            this.previewDisplayUrl = null;
            this.previewLoading = false;
            this.previewMaximized = false;
            this.previewZoomAnim = '';
            this.clearPreviewZoomTimer();
        },
        clearPreviewZoomTimer() {
            if (this.previewZoomTimer) {
                clearTimeout(this.previewZoomTimer);
                this.previewZoomTimer = null;
            }
        },
        togglePreviewMaximize() {
            var self = this;
            var goingFullscreen = !this.previewMaximized;
            this.previewZoomAnim = '';
            this.clearPreviewZoomTimer();
            this.$nextTick(function () {
                self.previewMaximized = goingFullscreen;
                self.previewZoomAnim = goingFullscreen ? 'in' : 'out';
                self.previewZoomTimer = setTimeout(function () {
                    self.previewZoomAnim = '';
                    self.previewZoomTimer = null;
                }, 460);
            });
        },
        attachmentDownloadFilename(attachment) {
            if (!attachment) return "download";
            var label = attachment.label ? String(attachment.label).trim() : "";
            if (!label) label = "download";
            var ext = attachment.file_extension ? String(attachment.file_extension).replace(/^\./, "") : "";
            if (ext) {
                var suffix = "." + ext.toLowerCase();
                if (label.toLowerCase().slice(-suffix.length) !== suffix) {
                    label = label + "." + ext;
                }
            }
            return label;
        },
        async downloadAttachment(attachment) {
            if (!attachment || !attachment.id) return;
            var filename = this.attachmentDownloadFilename(attachment);
            await this.forceDownload(attachment.id, filename);
        },
        async downloadAllAttachments(item) {
            if (!item || !item.id || !this.canDownloadProcessItem(item)) return;
            this.downloadingAll = true;
            try {
                var response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation GetProcessItemAttachmentsArchive($id: ID!) {
                            getProcessItemAttachmentsArchive(id: $id) {
                                error
                                error_code
                                message
                                data {
                                    content
                                    file_type
                                    filename
                                    attachments_count
                                }
                            }
                        }
                    `,
                    variables: { id: item.id },
                });

                var payload = response.data.getProcessItemAttachmentsArchive;
                if (!payload || payload.error || !payload.data || !payload.data.content) {
                    throw new Error((payload && payload.message) || "Failed to download attachments.");
                }

                var base64Content = payload.data.content;
                var binaryString = atob(base64Content);
                var bytes = new Uint8Array(binaryString.length);
                for (var i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                var mime = payload.data.file_type || "application/zip";
                var blob = new Blob([bytes], { type: mime });
                var blobUrl = window.URL.createObjectURL(blob);
                var link = document.createElement("a");
                link.href = blobUrl;
                link.download = payload.data.filename || (item.title ? item.title + ".zip" : "attachments.zip");
                link.style.display = "none";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (error) {
                console.error("Error downloading attachments archive:", error);
                this.toastError((error && error.message) || "Failed to download attachments. Please try again.");
            } finally {
                this.downloadingAll = false;
            }
        },
        async forceDownload(attachmentId, filename) {
            this.downloading = true;
            try {
                var blobResult = await this.fetchAttachmentContent(attachmentId);
                if (!blobResult || !blobResult.blob) {
                    throw new Error("Failed to download file.");
                }

                var blobUrl = window.URL.createObjectURL(blobResult.blob);
                var link = document.createElement("a");
                link.href = blobUrl;
                link.download = blobResult.filename || filename || "download";
                link.style.display = "none";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (error) {
                console.error("Error downloading file:", error);
                this.toastError((error && error.message) || "Failed to download the file. Please try again.");
            } finally {
                this.downloading = false;
            }
        },
        attachmentDisplayUrl(att) {
            if (!att || !att.id) return null;
            return this.attachmentBlobUrls[String(att.id)] || null;
        },
        attachmentThumbLoading(attachmentId) {
            if (!attachmentId) return false;
            return !!this.attachmentThumbLoadingIds[String(attachmentId)];
        },
        loadSelectedItemThumbnails() {
            var item = this.selectedItem;
            if (!item || !item.attachments) return;
            var self = this;
            for (var i = 0; i < item.attachments.length; i++) {
                var att = item.attachments[i];
                if (att && att.is_image && att.id) {
                    self.fetchAttachmentBlobUrl(att.id).catch(function () {
                        /* ignore thumbnail errors */
                    });
                }
            }
        },
        hydrateFormExistingPreviews() {
            var self = this;
            var rows = this.form.attachments || [];
            for (var i = 0; i < rows.length; i++) {
                (function (rowIndex) {
                    var row = rows[rowIndex];
                    if (row.existing && row.existing.is_image && row.existing.id) {
                        self.fetchAttachmentBlobUrl(row.existing.id)
                            .then(function (url) {
                                if (self.form.attachments[rowIndex]) {
                                    self.form.attachments[rowIndex].previewUrl = url;
                                }
                            })
                            .catch(function () {
                                /* ignore preview errors */
                            });
                    }
                })(i);
            }
        },
        async fetchAttachmentContent(attachmentId) {
            var response = await this.$apollo.mutate({
                mutation: gql`
                    mutation GetProcessItemAttachmentContent($id: ID!) {
                        getProcessItemAttachmentContent(id: $id) {
                            error
                            error_code
                            message
                            data {
                                content
                                file_type
                                filename
                                attachment {
                                    id
                                    original_filename
                                    file_type
                                }
                            }
                        }
                    }
                `,
                variables: { id: attachmentId },
            });

            var payload = response.data.getProcessItemAttachmentContent;
            if (!payload || payload.error || !payload.data || !payload.data.content) {
                throw new Error((payload && payload.message) || "Failed to fetch file content.");
            }

            var base64Content = payload.data.content;
            var binaryString = atob(base64Content);
            var bytes = new Uint8Array(binaryString.length);
            for (var i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            var mime = payload.data.file_type || "application/octet-stream";
            var blob = new Blob([bytes], { type: mime });

            return {
                blob: blob,
                mime: mime,
                filename: payload.data.filename || "download",
            };
        },
        async fetchAttachmentBlobUrl(attachmentId) {
            var cacheKey = String(attachmentId);
            if (this.attachmentBlobUrls[cacheKey]) {
                return this.attachmentBlobUrls[cacheKey];
            }

            this.attachmentThumbLoadingIds[cacheKey] = true;
            try {
                var blobResult = await this.fetchAttachmentContent(attachmentId);
                var blobUrl = window.URL.createObjectURL(blobResult.blob);
                this.attachmentBlobUrls[cacheKey] = blobUrl;
                return blobUrl;
            } finally {
                delete this.attachmentThumbLoadingIds[cacheKey];
            }
        },
        revokeAttachmentBlobUrls() {
            var keys = Object.keys(this.attachmentBlobUrls || {});
            for (var i = 0; i < keys.length; i++) {
                var url = this.attachmentBlobUrls[keys[i]];
                if (url) {
                    try {
                        window.URL.revokeObjectURL(url);
                    } catch (e) {
                        /* ignore */
                    }
                }
            }
            this.attachmentBlobUrls = {};
            this.attachmentThumbLoadingIds = {};
        },
        toastSuccess(text) {
            if (!this.$swal) return;
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            Toast.fire({
                icon: "success",
                title: text,
            });
        },
        toastError(text) {
            if (this.$swal) {
                this.$swal.fire({ icon: "error", title: "Error", text: text });
            }
        },
    },
};
</script>

<style scoped>
.proc-hero {
    background: linear-gradient(135deg, #ecfdf5 0%, #ffffff 52%, #f0fdf4 100%);
    border-color: #d1fae5;
}

.proc-hero__eyebrow {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #047857;
}

.proc-stat {
    display: inline-flex;
    flex-direction: column;
    min-width: 4.5rem;
    padding: 0.55rem 0.85rem;
    border-radius: 0.75rem;
    border: 1px solid #d1fae5;
    background: rgba(255, 255, 255, 0.9);
}

.proc-stat__value {
    font-size: 1.15rem;
    font-weight: 750;
    color: #0f172a;
    line-height: 1.1;
}

.proc-stat__label {
    font-size: 0.68rem;
    font-weight: 650;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.proc-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.55rem 0.95rem;
    border-radius: 0.7rem;
    font-size: 0.8125rem;
    font-weight: 650;
    border: 1px solid transparent;
}

.proc-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.proc-btn--primary {
    color: #fff;
    background: linear-gradient(180deg, #059669 0%, #047857 100%);
    box-shadow: 0 8px 18px -10px rgba(5, 150, 105, 0.55);
}

.proc-btn--ghost {
    color: #334155;
    background: #f8fafc;
    border-color: #e2e8f0;
}

.proc-btn--danger {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
}

.proc-empty {
    text-align: center;
    padding: 4rem 1.5rem;
    border-radius: 1.1rem;
    border: 1px dashed #cbd5e1;
    background: #fff;
}

.proc-empty__icon {
    width: 3.4rem;
    height: 3.4rem;
    margin: 0 auto 0.9rem;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #d1fae5;
    color: #047857;
    font-size: 1.3rem;
}

.proc-empty__title {
    font-size: 1.15rem;
    font-weight: 750;
    color: #0f172a;
}

.proc-empty__text {
    margin-top: 0.45rem;
    max-width: 32rem;
    margin-left: auto;
    margin-right: auto;
    color: #64748b;
    font-size: 0.9rem;
    line-height: 1.5;
}

.proc-shell {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    min-height: 34rem;
}

@media (min-width: 960px) {
    .proc-shell {
        grid-template-columns: 20rem minmax(0, 1fr);
        gap: 1.15rem;
    }
}

.proc-rail {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1.1rem;
    box-shadow: 0 12px 28px -20px rgba(15, 23, 42, 0.35);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.proc-rail__head {
    padding: 0.9rem;
    border-bottom: 1px solid #f1f5f9;
}

.proc-search {
    position: relative;
}

.proc-search__icon {
    position: absolute;
    left: 0.8rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.8rem;
}

.proc-search__input {
    width: 100%;
    padding: 0.55rem 0.75rem 0.55rem 2.1rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.7rem;
    font-size: 0.8125rem;
    outline: none;
}

.proc-search__input:focus {
    border-color: #34d399;
    box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.18);
}

.proc-rail__list {
    padding: 0.55rem;
    overflow: auto;
    max-height: 28rem;
}

@media (min-width: 960px) {
    .proc-rail__list {
        max-height: none;
        flex: 1;
    }
}

.proc-rail__item {
    width: 100%;
    text-align: left;
    padding: 0.8rem 0.85rem;
    border-radius: 0.8rem;
    border: 1px solid transparent;
    background: transparent;
    margin-bottom: 0.35rem;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.proc-rail__item:hover {
    background: #f8fafc;
}

.proc-rail__item--active {
    background: #ecfdf5;
    border-color: #a7f3d0;
}

.proc-rail__item-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
}

.proc-rail__title {
    font-size: 0.875rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.35;
}

.proc-rail__count {
    flex-shrink: 0;
    min-width: 1.5rem;
    height: 1.5rem;
    padding: 0 0.4rem;
    border-radius: 999px;
    background: #d1fae5;
    color: #047857;
    font-size: 0.7rem;
    font-weight: 750;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.proc-rail__desc {
    margin-top: 0.3rem;
    font-size: 0.72rem;
    color: #64748b;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.proc-rail__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: 0.45rem;
    font-size: 0.68rem;
    color: #94a3b8;
    line-height: 1.3;
}

.proc-rail__meta-icon {
    font-size: 0.65rem;
    color: #cbd5e1;
}

.proc-rail__meta-dot {
    color: #cbd5e1;
}

.proc-rail__empty {
    padding: 1.5rem 0.75rem;
    text-align: center;
    color: #94a3b8;
    font-size: 0.8125rem;
}

.proc-rail__pager {
    padding: 0.75rem 0.85rem 0.95rem;
    border-top: 1px solid #f1f5f9;
}

.proc-stage {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 1.1rem;
    box-shadow: 0 12px 28px -20px rgba(15, 23, 42, 0.35);
    padding: 1.15rem 1.2rem 1.35rem;
    min-height: 28rem;
}

.proc-stage__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.85rem;
    margin-bottom: 1.1rem;
    padding-bottom: 0.95rem;
    border-bottom: 1px solid #f1f5f9;
}

.proc-stage__title {
    font-size: 1.2rem;
    font-weight: 750;
    color: #0f172a;
}

.proc-stage__desc {
    margin-top: 0.3rem;
    color: #64748b;
    font-size: 0.875rem;
    line-height: 1.45;
}

.proc-stage__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
}

.proc-meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.35rem 0.65rem;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    font-size: 0.75rem;
    color: #475569;
}

.proc-meta-chip__icon-wrap {
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ecfdf5;
    color: #059669;
    font-size: 0.62rem;
    flex-shrink: 0;
}

.proc-meta-chip__icon-wrap--time {
    background: #eff6ff;
    color: #2563eb;
}

.proc-meta-chip__text strong {
    color: #0f172a;
    font-weight: 700;
}

.proc-menu {
    position: relative;
    flex-shrink: 0;
}

.proc-menu__trigger {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.65rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.proc-menu__trigger:hover,
.proc-menu__trigger--open {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
}

.proc-menu__dropdown {
    position: absolute;
    top: calc(100% + 0.35rem);
    right: 0;
    min-width: 9.5rem;
    padding: 0.35rem;
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    box-shadow: 0 14px 28px -16px rgba(15, 23, 42, 0.4);
    z-index: 20;
}

.proc-menu__item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.55rem;
    text-align: left;
    padding: 0.55rem 0.7rem;
    border-radius: 0.55rem;
    border: 0;
    background: transparent;
    color: #334155;
    font-size: 0.8125rem;
    font-weight: 650;
}

.proc-menu__item:hover {
    background: #f1f5f9;
}

.proc-menu__item--danger {
    color: #dc2626;
}

.proc-menu__item--danger:hover {
    background: #fef2f2;
}

.proc-menu__item-icon {
    width: 0.85rem;
    font-size: 0.75rem;
    opacity: 0.9;
}

.proc-att-empty {
    padding: 0.9rem 1rem;
    border-radius: 0.75rem;
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    color: #94a3b8;
    font-size: 0.8125rem;
}

.proc-stage__empty {
    padding: 4rem 1rem;
    text-align: center;
    color: #94a3b8;
    font-size: 0.9rem;
}

.proc-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(13.5rem, 1fr));
    gap: 1rem;
}

.proc-tile {
    position: relative;
    text-align: left;
    border: 1px solid #e2e8f0;
    border-radius: 0.95rem;
    overflow: hidden;
    background: #fff;
    transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.proc-tile:hover {
    transform: translateY(-2px);
    border-color: #a7f3d0;
    box-shadow: 0 16px 28px -18px rgba(16, 185, 129, 0.45);
}

.proc-tile__hit {
    display: block;
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: inherit;
}

.proc-tile__actions {
    position: absolute;
    top: 0.55rem;
    right: 0.55rem;
    z-index: 3;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
}

.proc-tile:hover .proc-tile__actions,
.proc-tile__actions--open,
.proc-tile__actions:focus-within {
    opacity: 1;
    pointer-events: auto;
}

.proc-tile-menu {
    position: relative;
}

.proc-tile-menu__trigger {
    width: 2rem;
    height: 2rem;
    border-radius: 0.55rem;
    border: 1px solid rgba(255, 255, 255, 0.45);
    background: rgba(255, 255, 255, 0.95);
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.18);
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.proc-tile-menu__trigger:hover,
.proc-tile-menu__trigger--open {
    background: #fff;
    border-color: #e2e8f0;
    color: #0f172a;
    transform: scale(1.04);
}

.proc-tile-menu__dropdown {
    min-width: 8.75rem;
}

.proc-tile--add {
    border-style: dashed;
    border-color: #86efac;
    background: #f0fdf4;
}

.proc-tile--add:hover {
    border-color: #34d399;
    background: #ecfdf5;
}

.proc-tile__media {
    position: relative;
    aspect-ratio: 4 / 3;
    background: #f8fafc;
    overflow: hidden;
}

.proc-tile__media--add {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%);
}

.proc-tile__add-icon {
    font-size: 1.75rem;
    color: #059669;
}

.proc-tile__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.proc-tile__pdf {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    color: #b91c1c;
    background: linear-gradient(180deg, #fff1f2 0%, #fee2e2 100%);
    font-size: 0.75rem;
    font-weight: 700;
}

.proc-tile__pdf-icon {
    font-size: 1.8rem;
}

.proc-tile__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    background: rgba(15, 23, 42, 0.45);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 650;
    opacity: 0;
    transition: opacity 0.15s ease;
}

.proc-tile:hover .proc-tile__overlay {
    opacity: 1;
}

.proc-tile__caption {
    padding: 0.7rem 0.8rem 0.85rem;
}

.proc-tile__label {
    font-size: 0.84rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
}

.proc-tile__meta {
    margin-top: 0.2rem;
    font-size: 0.7rem;
    color: #94a3b8;
}

.proc-tile__upload {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: 0.45rem;
    padding-top: 0.45rem;
    border-top: 1px solid #f1f5f9;
    font-size: 0.68rem;
    color: #94a3b8;
    line-height: 1.35;
}

.proc-tile__upload-icon {
    font-size: 0.62rem;
    color: #86efac;
}

.proc-tile__upload-dot {
    color: #cbd5e1;
}

/* Add / Edit process item modal — aligned with Master Contact List */
.proc-fade-enter-active,
.proc-fade-leave-active {
    transition: opacity 0.2s ease;
}

.proc-fade-enter-from,
.proc-fade-leave-to {
    opacity: 0;
}

.proc-form-modal-header {
    padding: 1.25rem 3rem 1.25rem 1.5rem;
    background: linear-gradient(135deg, #064e3b 0%, #047857 45%, #059669 100%);
    color: #fff;
    border-radius: 1rem 1rem 0 0;
    box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.proc-form-modal-header--fullscreen {
    border-radius: 0;
}

.proc-form-modal-header--with-controls {
    padding-right: 5.5rem;
}

.proc-form-modal-header-inner {
    display: flex;
    align-items: center;
    gap: 0.875rem;
}

.proc-form-modal-header-icon-wrap {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

.proc-form-modal-header-icon {
    font-size: 1.35rem;
    opacity: 0.95;
}

.proc-form-modal-header-title {
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0;
    line-height: 1.3;
}

.proc-form-modal-header-sub {
    margin: 0.2rem 0 0 0;
    font-size: 0.8125rem;
    color: rgba(209, 250, 229, 0.95);
    line-height: 1.35;
}

.proc-form-modal-shell {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.proc-form-modal-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 1.5rem;
    background: linear-gradient(180deg, #f8fafc 0%, #ecfdf5 100%);
}

.proc-form-modal-scroll--compact {
    padding: 1.25rem 1.5rem;
}

.proc-form-filename-hint {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0.5rem 0 0 0;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.35;
}

.proc-form-filename-hint__icon {
    font-size: 0.7rem;
    color: #94a3b8;
    flex-shrink: 0;
}

.proc-form-modal-footer {
    flex-shrink: 0;
    padding: 1rem 1.5rem 1.25rem;
    background: #fff;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.06);
    border-radius: 0 0 1rem 1rem;
}

.proc-form-modal-footer--fullscreen {
    border-radius: 0;
}

.proc-form-modal-card {
    background: #fff;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04);
    padding: 1.25rem;
    margin-bottom: 1rem;
}

.proc-form-modal-card--attachments {
    margin-bottom: 1rem;
}

.proc-form-modal-card__head {
    margin-bottom: 1rem;
}

.proc-form-modal-card__head--row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
}

.proc-form-modal-card__title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
}

.proc-form-modal-card__optional {
    font-weight: 500;
    color: #94a3b8;
}

.proc-form-modal-card__sub {
    margin: 0.25rem 0 0 0;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.4;
}

.proc-form-modal-fields {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
}

.proc-form-field {
    min-width: 0;
}

.proc-form-label {
    display: block;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 0.25rem;
}

.proc-form-required {
    color: #dc2626;
}

.proc-form-input {
    width: 100%;
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    color: #1e293b;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.625rem;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.proc-form-input:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.15);
}

.proc-form-textarea {
    resize: vertical;
    min-height: 5.5rem;
}

.proc-form-modal-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 0.85rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 0.625rem;
    flex-shrink: 0;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.proc-form-modal-add-btn:hover {
    background: #d1fae5;
    border-color: #6ee7b7;
}

.proc-form-att-empty {
    text-align: center;
    padding: 1.75rem 1rem;
    border-radius: 0.85rem;
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
}

.proc-form-att-empty__icon {
    width: 2.75rem;
    height: 2.75rem;
    margin: 0 auto 0.65rem;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ecfdf5;
    color: #059669;
    font-size: 1.15rem;
}

.proc-form-att-empty__title {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 650;
    color: #334155;
}

.proc-form-att-empty__text {
    margin: 0.35rem 0 0 0;
    font-size: 0.75rem;
    color: #94a3b8;
    line-height: 1.4;
}

.proc-form-att-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.proc-form-att-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.875rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    background: #f8fafc;
}

.proc-form-att-row__preview {
    width: 3.25rem;
    height: 3.25rem;
    border-radius: 0.65rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.proc-form-att-row__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.proc-form-att-row__pdf-icon {
    font-size: 1.5rem;
    color: #dc2626;
}

.proc-form-att-row__file-icon {
    font-size: 1.35rem;
    color: #94a3b8;
}

.proc-form-att-row__fields {
    flex: 1;
    min-width: 0;
}

.proc-form-file-pick {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-top: 0.5rem;
    padding: 0.55rem 0.75rem;
    border-radius: 0.625rem;
    border: 1px dashed #94a3b8;
    background: #fff;
    font-size: 0.8125rem;
    color: #475569;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;
}

.proc-form-file-pick:hover {
    border-color: #059669;
    background: #f0fdf4;
}

.proc-form-file-pick__icon {
    font-size: 0.75rem;
    color: #059669;
    flex-shrink: 0;
}

.proc-form-file-pick__text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.proc-form-error {
    margin: 0.35rem 0 0 0;
    font-size: 0.75rem;
    color: #b91c1c;
}

.proc-form-att-row__remove {
    width: 2rem;
    height: 2rem;
    border-radius: 0.55rem;
    border: 1px solid #fecaca;
    background: #fff;
    color: #dc2626;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.proc-form-att-row__remove:hover {
    background: #fef2f2;
    border-color: #f87171;
}

.proc-form-att-row__remove:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
}

.proc-form-modal-actions {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    margin-bottom: 0;
}

@media (min-width: 400px) {
    .proc-form-modal-actions {
        flex-direction: row;
        gap: 0.75rem;
    }
}

.proc-form-modal-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 0.625rem;
    border: none;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.proc-form-modal-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
}

.proc-form-modal-btn-primary {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    color: #fff;
    box-shadow: 0 4px 14px rgba(5, 150, 105, 0.35);
}

.proc-form-modal-btn-primary:not(:disabled):hover {
    background: linear-gradient(135deg, #047857 0%, #065f46 100%);
    box-shadow: 0 6px 20px rgba(5, 150, 105, 0.45);
    transform: translateY(-1px);
}

.proc-form-modal-btn-secondary {
    background: #fff;
    color: #334155;
    border: 2px solid #e2e8f0;
}

.proc-form-modal-btn-secondary:not(:disabled):hover {
    border-color: #cbd5e1;
    background: #f8fafc;
}

.proc-form-modal-hint {
    margin: 0 0 0.5rem 0;
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.5;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 0.5rem;
    border: 1px dashed #cbd5e1;
}

.proc-form-modal-hint-icon {
    margin-right: 0.25rem;
    color: #f59e0b;
}

/* Preview modal */
.proc-preview-modal-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 1.25rem 1.5rem;
    background: linear-gradient(180deg, #f8fafc 0%, #ecfdf5 100%);
}

.proc-preview-modal-scroll--maximized {
    padding: 0.75rem 1rem 1rem;
}

.proc-preview-modal-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
    flex-shrink: 0;
}

.proc-meta-chip__icon-wrap--type {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1d4ed8;
}

.proc-meta-chip__icon-wrap--size {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #b45309;
}

.proc-preview-modal-frame {
    flex: 1 1 auto;
    min-height: 0;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.6), 0 4px 16px rgba(15, 23, 42, 0.06);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

.proc-preview-modal-frame--image {
    padding: 1rem;
    background-color: #f1f5f9;
    background-image:
        linear-gradient(45deg, #e2e8f0 25%, transparent 25%),
        linear-gradient(-45deg, #e2e8f0 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #e2e8f0 75%),
        linear-gradient(-45deg, transparent 75%, #e2e8f0 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
}

.proc-preview-modal-frame--pdf {
    background: #fff;
    padding: 0;
}

.proc-preview-modal-img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
}

.proc-preview-modal-iframe {
    width: 100%;
    height: 100%;
    border: 0;
    background: #fff;
}

.proc-preview-modal-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2rem;
    color: #64748b;
    text-align: center;
}

.proc-preview-modal-state__icon {
    font-size: 1.75rem;
    color: #059669;
}

.proc-preview-modal-state__icon--warn {
    color: #f59e0b;
}

.proc-preview-modal-state__text {
    font-size: 0.875rem;
    color: #64748b;
}

.proc-modal {
    width: 100%;
    max-width: 40rem;
    max-height: 90vh;
    overflow: auto;
    background: #fff;
    border-radius: 1rem;
    box-shadow: 0 24px 48px -20px rgba(15, 23, 42, 0.45);
}

.proc-modal--sm {
    max-width: 26rem;
}

.proc-modal__header,
.proc-modal__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem 1.15rem;
    border-bottom: 1px solid #f1f5f9;
}

.proc-modal__footer {
    border-bottom: 0;
    border-top: 1px solid #f1f5f9;
    justify-content: flex-end;
}

.proc-modal__title {
    font-size: 1.05rem;
    font-weight: 750;
    color: #0f172a;
}

.proc-modal__body {
    padding: 1.15rem;
}

.proc-field {
    margin-bottom: 1rem;
}

.proc-label {
    display: block;
    font-size: 0.8125rem;
    font-weight: 650;
    color: #334155;
    margin-bottom: 0.4rem;
}

.proc-hint {
    font-size: 0.75rem;
    color: #94a3b8;
}

.proc-input {
    width: 100%;
    padding: 0.6rem 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.65rem;
    font-size: 0.875rem;
    outline: none;
}

.proc-input:focus {
    border-color: #34d399;
    box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.18);
}

.proc-textarea {
    resize: vertical;
    min-height: 5rem;
}

.proc-att-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.proc-att-row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    background: #f8fafc;
}

.proc-att-row__preview {
    width: 3.25rem;
    height: 3.25rem;
    border-radius: 0.65rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.proc-att-row__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.proc-att-row__fields {
    flex: 1;
    min-width: 0;
}

.proc-file-pick {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px dashed #94a3b8;
    background: #fff;
    font-size: 0.75rem;
    color: #475569;
    cursor: pointer;
    max-width: 100%;
}

.proc-file-pick span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.proc-icon-btn {
    width: 2rem;
    height: 2rem;
    border-radius: 0.55rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.proc-icon-btn--danger {
    color: #dc2626;
}

.proc-icon-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.proc-tile__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    min-height: 7rem;
    background: linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%);
}

.proc-tile__loading-icon {
    font-size: 1.25rem;
    color: #64748b;
}
</style>
